<script setup lang="ts">
  import { type Component } from "vue";
  import { Spinner } from "../Spinner";

  const {
    size = "m",
    variant = "primary",
    disabled,
    iconBefore,
    iconAfter,
    asSquare,
    isLoading,
    isActive,
  } = defineProps<{
    size?: "s" | "m" | "l";
    variant?: "primary" | "secondary" | "tertiary" | "negative";
    isLoading?: boolean;
    disabled?: boolean;
    asSquare?: boolean;
    /** @deprecated Используйте слот iconBefore вместо этого свойства. */
    iconBefore?: Component;
    /** @deprecated Используйте слот iconAfter вместо этого свойства. */
    iconAfter?: Component;
    isActive?: boolean;
  }>();

  defineSlots<{
    default(): void;
    iconBefore(): void;
    iconAfter(): void;
  }>();
</script>

<template>
  <button
    :class="['button', `button--size-${size}`, `button--variant-${variant}`, { 'button--rect': !asSquare }, { 'button--active': isActive }]"
    :disabled="disabled"
  >
    <div class="button__content">
      <Spinner v-if="isLoading" class="button__spinner" />
      <div class="button__inner" :class="{ 'button__inner--hidden': isLoading }">
        <component :is="iconBefore" v-if="iconBefore" />
        <slot name="iconBefore" />
        <slot />
        <slot name="iconAfter" />
        <component :is="iconAfter" v-if="iconAfter" />
      </div>
    </div>
  </button>
</template>

<style scoped>
  @media (hover: hover) {
    .button--variant-primary:hover {
      background-color: var(--sf-button-primary-background-hover);
    }

    .button--variant-secondary:hover {
      background-color: var(--sf-button-secondary-background-hover);
    }

    .button--variant-tertiary:hover {
      background-color: var(--sf-button-tertiary-background-hover);
    }

    .button--variant-negative:hover {
      background-color: var(--sf-button-negative-background-hover);
    }

    .button--variant-negative:hover .button__inner {
      color: var(--sf-button-negative-foreground-hover);
    }

    .button--variant-negative:hover svg {
      stroke: var(--sf-button-negative-foreground-hover);
    }
  }

  .button {
    border: none;
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    box-sizing: border-box;
    border-radius: 8px;
    box-sizing: border-box;
    overflow: hidden;
  }

  .button .button__spinner {
    position: absolute;
    width: 24px;
    height: 24px;
  }

  .button__content {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  .button__inner {
    display: flex;
    gap: 8px;
    align-items: center;
    font-weight: 580;
    letter-spacing: 0.16px;
    line-height: 20px;
    font-size: 16px;

    & svg {
      pointer-events: none;
    }
  }

  .button__inner--hidden {
    visibility: hidden;
  }

  .button--size-l {
    border-radius: 12px;
    height: 52px;
    width: 52px;

    & svg {
      width: 24px;
      height: 24px;
    }
  }

  .button--size-m {
    border-radius: 12px;
    height: 48px;
    width: 48px;

    & svg {
      width: 24px;
      height: 24px;
    }
  }

  .button--size-s {
    border-radius: 8px;
    height: 44px;
    width: 44px;

    & .button__inner {
      font-size: 14px;
    }

    & svg {
      width: 20px;
      height: 20px;
    }
  }

  .button--variant-primary {
    background-color: var(--sf-button-primary-background-default);

    &.button--active {
      background-color: var(--sf-button-primary-background-pressed);
    }

    &:active {
      background-color: var(--sf-button-primary-background-pressed);
    }

    &:disabled {
      background-color: var(--sf-button-primary-background-disabled);
      cursor: not-allowed;
    }

    & .button__inner {
      color: var(--sf-base-text-white);
    }

    & svg {
      stroke: var(--sf-base-icon-white);
    }
  }

  .button--variant-secondary {
    background-color: var(--sf-button-secondary-background-default);
    border: 1px solid var(--sf-base-border-primary);

    &.button--active {
      background-color: var(--sf-button-secondary-background-pressed);
    }

    &:active {
      background-color: var(--sf-button-secondary-background-pressed);
    }

    &:disabled {
      background-color: var(--sf-button-secondary-background-disabled);
      border-color: var(--sf-base-border-secondary);
      cursor: not-allowed;

      & .button__inner {
        color: var(--sf-base-text-secondary);
      }

      & svg {
        stroke: var(--sf-base-icon-secondary);
      }
    }

    & .button__inner {
      color: var(--sf-base-text-primary);
    }
  }

  .button--variant-tertiary {
    background-color: var(--sf-button-tertiary-background-default);

    &.button--active {
      background-color: var(--sf-button-tertiary-background-pressed);
    }

    &:active {
      background-color: var(--sf-button-tertiary-background-pressed);
    }

    &:disabled {
      background-color: var(--sf-button-tertiary-background-disabled);
      color: var(--sf-base-text-secondary);
      cursor: not-allowed;

      & svg {
        stroke: var(--sf-base-icon-secondary);
      }
    }
  }

  .button--variant-negative {
    background-color: var(--sf-button-negative-background-default);

    &.button--active {
      background-color: var(--sf-button-negative-background-pressed);
    }

    &:active {
      background-color: var(--sf-button-negative-background-pressed);
    }

    &:disabled {
      background-color: var(--sf-button-negative-background-disabled);
      cursor: not-allowed;

      & .button__inner {
        color: var(--sf-button-negative-foreground-disabled);
      }

      & svg {
        stroke: var(--sf-button-negative-foreground-disabled);
      }
    }

    & .button__inner {
      color: var(--sf-button-negative-foreground-default);
    }
  }

  .button--rect {
    padding: 0 24px;
    width: fit-content;
  }
</style>
